/* ==========================================================================
   Umya · Vista de categoría — carga progresiva, orden, segunda foto y afinar
   19-ago-2026 · afinar añadido el 20-ago-2026 · reescrito el 20-ago-2026

   Sin guion la página sigue funcionando: el enlace dentro de <noscript> lleva
   a la página siguiente, el selector de orden se envía solo al cambiarlo, las
   pastillas de "afinar" no se pintan y el panel de filtros de móvil se queda
   abierto (que es como estaba antes de que existiera el botón).

   Lo que cambia respecto a la primera versión:

   · La colección entera se trae en segundo plano nada más entrar, a memoria,
     sin tocar la rejilla. Eso permite pintar los recuentos de cada pastilla
     al entrar en vez de al primer toque, y que al pulsar una talla la
     respuesta sea inmediata.
   · La talla elegida viaja en la dirección (umya-size). Antes se perdía al
     pulsar un tramo de precio, porque el precio recarga la página desde el
     servidor y la talla sólo vivía en el navegador.
   · Se pueden marcar varias tallas a la vez.
   ========================================================================== */

(() => {
  const CLAVE_TALLA = 'umya-size';
  const CLAVE_TIPO = 'umya-type';
  const TANDA = 48;

  const raiz = () => document.querySelector('.umya-coleccion');

  /* --- Segunda foto al pasar el ratón -------------------------------------
     Nacen sin src para no descargar una foto por tarjeta en móvil, donde no
     hay ratón y nadie las va a ver. Se baja al primer roce. */
  const bajar = (img) => {
    if (!img || !img.hasAttribute('data-tarde')) return;
    img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.removeAttribute('data-tarde');
  };

  const escuchar = (contenedor) => {
    if (!contenedor || contenedor.dataset.escuchando) return;
    contenedor.dataset.escuchando = '1';
    const alRozar = (e) => {
      const tarjeta = e.target.closest?.('.umya-tarjeta');
      if (tarjeta) bajar(tarjeta.querySelector('.umya-tarjeta__foto--dos[data-tarde]'));
    };
    contenedor.addEventListener('mouseover', alRozar);
    contenedor.addEventListener('focusin', alRozar);
  };

  /* --- Dirección -----------------------------------------------------------
     La dirección que escribe Liquid trae la página y los filtros, pero NO los
     parámetros que van solo en la barra: preview_theme_id el primero. Sin él,
     la tanda siguiente la sirve el tema PUBLICADO (hoy Brooklyn) y vuelve sin
     una sola pieza. Se parte de la dirección actual y solo se le cambia lo
     que toca. */
  const conParametrosDeLaBarra = (destino) => {
    const nueva = new URL(destino, window.location.origin);
    const actual = new URL(window.location.href);
    actual.searchParams.forEach((valor, clave) => {
      if (!nueva.searchParams.has(clave)) nueva.searchParams.set(clave, valor);
    });
    return nueva;
  };

  const leerLista = (clave) => {
    const bruto = new URL(window.location.href).searchParams.get(clave) || '';
    return bruto.split(',').map((v) => v.trim()).filter(Boolean);
  };

  /* ======================================================================
     Estado de la vista
     ====================================================================== */
  const crearEstado = (ambito) => ({
    ambito,
    // <li> ya traídos del servidor pero todavía no puestos en la rejilla.
    buffer: [],
    urlSiguiente: ambito.querySelector('[data-mas]')?.dataset.url || '',
    completa: !ambito.querySelector('[data-mas]'),
    trayendo: null,
    tallas: leerLista(CLAVE_TALLA),
    tipos: leerLista(CLAVE_TIPO),
  });

  /* --- Traer una tanda a memoria ------------------------------------------
     Devuelve los <li> y la dirección de la siguiente. No toca el DOM vivo. */
  const pedirTanda = async (url) => {
    const respuesta = await fetch(conParametrosDeLaBarra(url).toString());
    if (!respuesta.ok) throw new Error(respuesta.status);
    const doc = new DOMParser().parseFromString(await respuesta.text(), 'text/html');
    const lis = Array.from(doc.querySelectorAll('[data-rejilla] > li'));
    if (!lis.length) throw new Error('sin piezas');
    return { lis, siguiente: doc.querySelector('[data-mas]')?.dataset.url || '' };
  };

  /* Traer TODO lo que falte, a memoria. Se llama en segundo plano al entrar y
     también al pulsar una pastilla, por si aún no había terminado. Una sola
     ejecución a la vez: las llamadas de después se enganchan a la promesa. */
  const traerTodo = (est) => {
    if (est.completa) return Promise.resolve();
    if (est.trayendo) return est.trayendo;

    est.trayendo = (async () => {
      // Tope de vueltas por si algo devolviera siempre la misma dirección:
      // son 12 tandas, muy por encima de la colección más grande (104).
      let vueltas = 0;
      while (est.urlSiguiente && vueltas < 12) {
        const { lis, siguiente } = await pedirTanda(est.urlSiguiente);
        est.buffer.push(...lis);
        est.urlSiguiente = siguiente;
        vueltas += 1;
      }
      est.completa = true;
    })();

    est.trayendo.catch(() => {}).then(() => {
      est.trayendo = null;
    });

    return est.trayendo;
  };

  /* Pasar del buffer a la rejilla. Sin cantidad, va todo. */
  const volcar = (est, cuantas) => {
    const rejilla = est.ambito.querySelector('[data-rejilla]');
    if (!rejilla || !est.buffer.length) {
      if (est.completa && !est.buffer.length) est.ambito.querySelector('.umya-coleccion__mas')?.remove();
      return;
    }
    const lote = cuantas ? est.buffer.splice(0, cuantas) : est.buffer.splice(0);
    lote.forEach((li) => rejilla.appendChild(li));
    escuchar(rejilla);
    if (est.completa && !est.buffer.length) est.ambito.querySelector('.umya-coleccion__mas')?.remove();
  };

  const cargarMas = (est) => {
    const boton = est.ambito.querySelector('[data-mas]');
    if (!boton || boton.dataset.atado) return;
    boton.dataset.atado = '1';

    boton.addEventListener('click', async () => {
      if (boton.disabled) return;

      // Si la precarga ya lo trajo, es instantáneo.
      if (est.buffer.length) {
        volcar(est, TANDA);
        return;
      }

      boton.disabled = true;
      boton.setAttribute('aria-busy', 'true');
      try {
        await traerTodo(est);
        volcar(est, TANDA);
        boton.disabled = false;
        boton.removeAttribute('aria-busy');
      } catch (e) {
        // Si algo falla, se manda a la página siguiente por las bravas en
        // vez de dejar un botón muerto.
        const destino = conParametrosDeLaBarra(est.urlSiguiente || boton.dataset.url);
        destino.searchParams.delete('section_id');
        window.location.href = destino.toString();
      }
    });
  };

  /* ======================================================================
     Afinar: talla de anillo, talla de puño y tipo de joya
     ======================================================================
     Regla que manda sobre todo lo demás: filtra sobre la colección ENTERA.
     Un filtro que solo mira las piezas cargadas miente, y miente hacia abajo,
     que es la peor forma de mentir en una tienda de piezas únicas: te dice
     que no hay tu talla cuando sí la hay. */

  const tarjetasDe = (est) => Array.from(est.ambito.querySelectorAll('[data-rejilla] > li'));
  const todasLasTarjetas = (est) => tarjetasDe(est).concat(est.buffer);

  const num = (v) => (v === '' || v === undefined || v === null ? null : parseFloat(v));

  const tramoDe = (valor) => {
    const partes = String(valor).split('-');
    return { min: num(partes[0]), max: num(partes[1]) };
  };

  const encaja = (li, tallas, tipos) => {
    if (!tallas.length && !tipos.length) return true;
    const enlace = li.querySelector('.umya-tarjeta');
    if (!enlace) return false;

    if (tipos.length && !tipos.includes(enlace.dataset.tipo || '')) return false;

    if (tallas.length) {
      const valor = parseFloat(enlace.dataset.talla);
      if (!Number.isFinite(valor)) return false;
      const alguno = tallas.some((t) => {
        const { min, max } = tramoDe(t);
        if (min !== null && valor < min) return false;
        if (max !== null && valor > max) return false;
        return true;
      });
      if (!alguno) return false;
    }

    return true;
  };

  /* --- La talla viaja en la dirección --------------------------------------
     Antes vivía solo en el navegador, así que al pulsar un tramo de precio
     (que recarga la página desde el servidor) se perdía: talla 52-54 daba 18
     piezas y al añadir el precio salían 25. Ahora se escribe en la barra y se
     pega a los enlaces de los filtros del servidor. */
  const escribirDireccion = (est) => {
    const url = new URL(window.location.href);
    if (est.tallas.length) url.searchParams.set(CLAVE_TALLA, est.tallas.join(','));
    else url.searchParams.delete(CLAVE_TALLA);
    if (est.tipos.length) url.searchParams.set(CLAVE_TIPO, est.tipos.join(','));
    else url.searchParams.delete(CLAVE_TIPO);
    window.history.replaceState({}, '', url.pathname + url.search);

    est.ambito.querySelectorAll('a[data-filtro-servidor]').forEach((a) => {
      if (!a.dataset.hrefBase) a.dataset.hrefBase = a.getAttribute('href');
      const destino = new URL(a.dataset.hrefBase, window.location.origin);
      destino.searchParams.delete(CLAVE_TALLA);
      destino.searchParams.delete(CLAVE_TIPO);
      if (est.tallas.length) destino.searchParams.set(CLAVE_TALLA, est.tallas.join(','));
      if (est.tipos.length) destino.searchParams.set(CLAVE_TIPO, est.tipos.join(','));
      a.setAttribute('href', destino.pathname + destino.search);
    });
  };

  const contarFiltros = (est) => {
    const servidor = est.ambito.querySelectorAll(
      '.umya-coleccion__filtros .umya-chip--activo:not([data-todo-servidor])'
    ).length;
    return est.tallas.length + est.tipos.length + servidor;
  };

  const pintarContador = (est) => {
    const marca = est.ambito.querySelector('[data-filtros-puestos]');
    if (!marca) return;
    const n = contarFiltros(est);
    marca.textContent = n ? String(n) : '';
    marca.hidden = !n;
  };

  const pintar = (est) => {
    const hay = est.tallas.length || est.tipos.length;
    let vistas = 0;
    tarjetasDe(est).forEach((li) => {
      const pasa = encaja(li, est.tallas, est.tipos);
      li.hidden = !pasa;
      if (pasa) vistas += 1;
    });

    const cuenta = est.ambito.querySelector('[data-cuenta]');
    if (cuenta) {
      if (!cuenta.dataset.original) cuenta.dataset.original = cuenta.textContent.trim();
      cuenta.textContent = hay ? `${vistas} ${vistas === 1 ? 'piece' : 'pieces'}` : cuenta.dataset.original;
    }

    const vacio = est.ambito.querySelector('[data-afinar-vacio]');
    if (vacio) vacio.hidden = !(hay && vistas === 0);

    // Con un afinado puesto ya está todo traído: el botón sobra y confunde.
    const mas = est.ambito.querySelector('.umya-coleccion__mas');
    if (mas) mas.hidden = Boolean(hay);

    pintarContador(est);
  };

  /* Recuento por pastilla. Cuenta sobre la colección entera —lo que está en
     la rejilla más lo que espera en memoria—, así que sale bien aunque la
     rejilla todavía enseñe la primera tanda. Una pastilla que se queda a cero
     se retira, para que nadie pulse un callejón sin salida. */
  const numerar = (est) => {
    const todas = todasLasTarjetas(est);
    if (!todas.length) return;

    est.ambito.querySelectorAll('[data-afinar-talla], [data-afinar-tipo]').forEach((chip) => {
      const esTalla = chip.hasAttribute('data-afinar-talla');
      const tallas = esTalla ? [chip.dataset.afinarTalla] : [];
      const tipos = esTalla ? [] : [chip.dataset.afinarTipo];
      const cuantas = todas.filter((li) => encaja(li, tallas, tipos)).length;

      if (!cuantas) {
        chip.hidden = true;
        return;
      }
      chip.hidden = false;
      let marca = chip.querySelector('.umya-chip__num');
      if (!marca) {
        marca = document.createElement('span');
        marca.className = 'umya-chip__num';
        chip.appendChild(marca);
      }
      marca.textContent = cuantas;
    });
  };

  const marcarChips = (est) => {
    est.ambito.querySelectorAll('[data-afinar-talla]').forEach((chip) => {
      const puesto = est.tallas.includes(chip.dataset.afinarTalla);
      chip.classList.toggle('umya-chip--activo', puesto);
      chip.setAttribute('aria-pressed', puesto ? 'true' : 'false');
    });
    est.ambito.querySelectorAll('[data-afinar-tipo]').forEach((chip) => {
      const puesto = est.tipos.includes(chip.dataset.afinarTipo);
      chip.classList.toggle('umya-chip--activo', puesto);
      chip.setAttribute('aria-pressed', puesto ? 'true' : 'false');
    });
    est.ambito.querySelectorAll('[data-afinar]').forEach((caja) => {
      const todo = caja.querySelector('[data-afinar-todo]');
      if (!todo) return;
      const alguno = caja.querySelector('.umya-chip--activo:not([data-afinar-todo])');
      todo.classList.toggle('umya-chip--activo', !alguno);
    });
  };

  /* Aplicar la selección: trae lo que falte, lo vuelca y pinta. */
  const aplicar = async (est) => {
    marcarChips(est);
    escribirDireccion(est);

    const hay = est.tallas.length || est.tipos.length;
    if (!hay) {
      pintar(est);
      return;
    }

    const estado = est.ambito.querySelector('[data-afinar-estado]');
    try {
      if (!est.completa) {
        if (estado) estado.textContent = 'Loading every piece…';
        await traerTodo(est);
      }
      volcar(est);
      if (estado) estado.textContent = '';
      pintar(est);
    } catch (e) {
      // Si la carga se corta, se dice y no se esconde nada: mejor la rejilla
      // entera que una selección a medias que parece completa.
      if (estado) estado.textContent = 'Could not load the full collection. Showing everything.';
      est.tallas = [];
      est.tipos = [];
      marcarChips(est);
      escribirDireccion(est);
      pintar(est);
    }
  };

  const afinar = (est) => {
    const cajas = Array.from(est.ambito.querySelectorAll('[data-afinar]'));
    if (!cajas.length) return;

    // Si no hay rejilla (colección agotada) las pastillas no pintan nada.
    if (!est.ambito.querySelector('[data-rejilla] > li')) {
      cajas.forEach((caja) => caja.remove());
      return;
    }

    cajas.forEach((caja) => {
      if (caja.dataset.atado) return;
      caja.dataset.atado = '1';
      const multiple = caja.dataset.multiple !== '0';

      caja.querySelectorAll('[data-afinar-talla]').forEach((chip) => {
        chip.addEventListener('click', () => {
          const valor = chip.dataset.afinarTalla;
          const puesto = est.tallas.includes(valor);
          if (!multiple) est.tallas = puesto ? [] : [valor];
          else if (puesto) est.tallas = est.tallas.filter((v) => v !== valor);
          else est.tallas = est.tallas.concat(valor);
          aplicar(est);
        });
      });

      caja.querySelectorAll('[data-afinar-tipo]').forEach((chip) => {
        chip.addEventListener('click', () => {
          const valor = chip.dataset.afinarTipo;
          const puesto = est.tipos.includes(valor);
          if (!multiple) est.tipos = puesto ? [] : [valor];
          else if (puesto) est.tipos = est.tipos.filter((v) => v !== valor);
          else est.tipos = est.tipos.concat(valor);
          aplicar(est);
        });
      });

      const todo = caja.querySelector('[data-afinar-todo]');
      if (todo) {
        todo.addEventListener('click', () => {
          if (caja.dataset.modo === 'tipo') est.tipos = [];
          else est.tallas = [];
          aplicar(est);
        });
      }
    });

    const limpiar = est.ambito.querySelector('[data-afinar-limpiar]');
    if (limpiar && !limpiar.dataset.atado) {
      limpiar.dataset.atado = '1';
      limpiar.addEventListener('click', () => {
        est.tallas = [];
        est.tipos = [];
        aplicar(est);
      });
    }
  };

  /* --- Orden ---------------------------------------------------------------
     Se parte de la dirección actual, que a estas alturas ya lleva la talla
     escrita, así que ordenar no la pierde. */
  const orden = (est) => {
    const select = est.ambito.querySelector('[data-orden]');
    if (!select || select.dataset.atado) return;
    select.dataset.atado = '1';
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  };

  /* --- Panel de filtros en móvil -------------------------------------------
     En escritorio el panel está siempre a la vista y el botón no existe. En
     móvil eran tres tiras deslizantes apiladas: 529 px hasta la primera joya.
     El CSS solo esconde el panel si esta clase está puesta, así que sin guion
     se queda abierto y nadie se queda sin filtros. */
  const panelMovil = (est) => {
    const mando = est.ambito.querySelector('[data-mando]');
    const boton = est.ambito.querySelector('[data-abrir-panel]');
    if (!mando || !boton || boton.dataset.atado) return;
    boton.dataset.atado = '1';

    const poner = (abierto) => {
      mando.classList.toggle('umya-coleccion__mando--abierto', abierto);
      boton.setAttribute('aria-expanded', abierto ? 'true' : 'false');
    };

    boton.addEventListener('click', () => {
      poner(!mando.classList.contains('umya-coleccion__mando--abierto'));
    });

    est.ambito.querySelector('[data-cerrar-panel]')?.addEventListener('click', () => {
      poner(false);
      est.ambito.querySelector('[data-rejilla]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  /* ====================================================================== */
  const arrancar = () => {
    const ambito = raiz();
    if (!ambito) return;

    const est = crearEstado(ambito);

    escuchar(ambito.querySelector('[data-rejilla]'));
    orden(est);
    cargarMas(est);
    afinar(est);
    panelMovil(est);

    // El chip "All" de los filtros del servidor no cuenta como filtro puesto.
    ambito.querySelector('.umya-coleccion__filtros a[data-filtro-servidor]')?.setAttribute('data-todo-servidor', '');

    marcarChips(est);
    escribirDireccion(est);
    numerar(est);
    pintarContador(est);

    // Si la dirección ya traía una talla (se llegó desde un filtro de precio,
    // o el enlace se compartió tal cual), se aplica sin que nadie pulse.
    if (est.tallas.length || est.tipos.length) aplicar(est);

    // Precarga en segundo plano: la colección entera a memoria, sin tocar la
    // rejilla. Es lo que permite que los recuentos salgan al entrar y que
    // pulsar una talla responda al instante. Se lanza cuando el navegador
    // está ocioso para no competir con la primera pantalla.
    //
    // Solo donde hay pastillas que numerar. En "Unique jewelry" (242 piezas)
    // no las hay, y precargar allí serían cinco peticiones seguidas nada más
    // entrar para no enseñar nada distinto.
    const hayPastillas = ambito.querySelector('[data-afinar-talla], [data-afinar-tipo]');
    if (!est.completa && hayPastillas) {
      const lanzar = () => {
        traerTodo(est)
          .then(() => numerar(est))
          .catch(() => {});
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(lanzar, { timeout: 3000 });
      else window.setTimeout(lanzar, 1200);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  // El editor de temas vuelve a pintar la sección al tocar un ajuste.
  document.addEventListener('shopify:section:load', arrancar);
})();
